/* Approvals module — sample data + rules ported from the prototype.
   Travel / training: Head of Unit -> Head of Department -> Admin.
   Claims: Head of Unit -> Head of Department -> Finance. */

export const FLOW_LABELS = {
  unit: {
    default: ["Head of Unit", "Head of Department", "Admin"],
    claim: ["Head of Unit", "Head of Department", "Finance"],
  },
  department: {
    default: ["Head of Department", "CEO", "Admin"],
    claim: ["Head of Department", "CEO", "Finance"],
  },
};

export const FLOW_STAGES = {
  unit: {
    default: ["hou", "hod", "admin"],
    claim: ["hou", "hod", "finance"],
  },
  department: {
    default: ["hod", "ceo", "admin"],
    claim: ["hod", "ceo", "finance"],
  },
};

export const approvalRoute = (request) => {
  const hierarchy = request.hierarchy === "department" ? "department" : "unit";
  const category = request.category === "claim" ? "claim" : "default";
  return {
    labels: FLOW_LABELS[hierarchy][category],
    stages: FLOW_STAGES[hierarchy][category],
  };
};

export const APPROVAL_REQUESTS = [
  {
    id: "TR-2026-091",
    category: "travel",
    typeLabel: "Travel Request",
    subtitle: "Singapore · ITB Asia 2026\n15–18 October 2026",
    requestor: { initials: "AC", name: "Ashley Chong", role: "Executive · Operations", bg: "#e0e7ff", color: "#4338ca" },
    submitted: "20 Jul 2026",
    amount: "4,100.00",
    stage: "hou",
  },
  {
    id: "TR-2026-087",
    category: "travel",
    typeLabel: "Travel Request",
    subtitle: "Kuala Lumpur · MICE Summit 2026\n03–05 September 2026",
    requestor: { initials: "JL", name: "John Lim", role: "Senior Executive · Finance", bg: "#fef3c7", color: "#b45309" },
    submitted: "18 Jul 2026",
    amount: "3,200.00",
    stage: "hod",
  },
  {
    id: "TR-2026-082",
    category: "travel",
    typeLabel: "Travel Request",
    subtitle: "Johor Bahru · Regional Sales Meeting\n25–26 August 2026",
    requestor: { initials: "SH", name: "Sarah Hui", role: "Executive · Marketing", bg: "#fce7f3", color: "#be185d" },
    submitted: "16 Jul 2026",
    amount: "980.00",
    stage: "admin",
  },
  {
    id: "TN-2026-044",
    category: "training",
    typeLabel: "Training Request",
    subtitle: "Successful Project Management\nMalaysian Institute of Management",
    requestor: { initials: "MR", name: "Melissa Richard", role: "Head of Department · Human Resource", bg: "#ede9fe", color: "#7c3aed" },
    submitted: "19 Jul 2026",
    amount: "2,450.00",
    stage: "hod",
    hierarchy: "department",
  },
  {
    id: "TN-2026-039",
    category: "training",
    typeLabel: "Training Request",
    subtitle: "Advanced Laravel API Integrations\nLaracasts / Online",
    requestor: { initials: "BW", name: "Benedict Wong", role: "Assistant Manager · Operations", bg: "#f1f5f9", color: "#334155" },
    submitted: "17 Jul 2026",
    amount: "1,200.00",
    stage: "hod",
  },
  {
    id: "CL-2026-091",
    category: "claim",
    typeLabel: "Cash Claim",
    subtitle: "Staff welfare and medical expenses\n3 claim items",
    requestor: { initials: "AC", name: "Ashley Chong", role: "Executive · Operations", bg: "#e0e7ff", color: "#4338ca" },
    submitted: "20 Jul 2026",
    amount: "7,992.00",
    stage: "hou",
  },
  {
    id: "CL-2026-083",
    category: "claim",
    typeLabel: "Local Travel Claim",
    subtitle: "Within Malaysia travel expenses\nMeal allowance attached",
    requestor: { initials: "JL", name: "John Lim", role: "Senior Executive · Finance", bg: "#fef3c7", color: "#b45309" },
    submitted: "18 Jul 2026",
    amount: "288.00",
    stage: "hod",
  },
  {
    id: "CL-2026-076",
    category: "claim",
    typeLabel: "Mileage Claim",
    subtitle: "Vehicle mileage reimbursement\n146.1 KM total",
    requestor: { initials: "SH", name: "Sarah Hui", role: "Executive · Marketing", bg: "#fce7f3", color: "#be185d" },
    submitted: "15 Jul 2026",
    amount: "5,994.00",
    stage: "finance",
  },
];

/* Labels and visual treatment for every supported approval stage. */
export const STAGE_BADGE = {
  hou: { cls: "status-supervisor", label: "Head of Unit Review" },
  hod: { cls: "status-hr", label: "Head of Dept Review" },
  ceo: { cls: "status-ceo", label: "CEO Review" },
  admin: { cls: "status-finance", label: "Admin Review" },
  finance: { cls: "status-finance", label: "Finance Review" },
  approved: { cls: "status-approved", label: "Fully Approved" },
  returned: { cls: "status-supervisor", label: "Returned for Revision" },
  rejected: { cls: "status-rejected", label: "Rejected" },
};

export const isDecided = (stage) => stage === "approved" || stage === "returned" || stage === "rejected";

/* Approve advances to the final reviewer for the request category. */
export const nextStage = (request) => {
  const { stages } = approvalRoute(request);
  const currentIndex = stages.indexOf(request.stage);
  if (currentIndex < 0 || currentIndex === stages.length - 1) return "approved";
  return stages[currentIndex + 1];
};

export const DECISION_CONFIG = {
  approve: {
    title: "Approve Request",
    confirmLabel: "Approve",
    confirmCls: "confirm-approve",
    noticeCls: "notice-approve",
    notice: "Upon approval, the request will move to the next approval stage. The final Admin or Finance approval completes the workflow.",
    remarksRequired: false,
  },
  return: {
    title: "Return for Revision",
    confirmLabel: "Return Request",
    confirmCls: "confirm-return",
    noticeCls: "notice-return",
    notice: "The request will be returned to the requestor for revision. Remarks are required.",
    remarksRequired: true,
  },
  reject: {
    title: "Reject Request",
    confirmLabel: "Reject Request",
    confirmCls: "confirm-reject",
    noticeCls: "notice-reject",
    notice: "The request will be rejected and the approval workflow will end. Remarks are required.",
    remarksRequired: true,
  },
};

/* Detail-page route per category (View action). */
export const detailPath = (category, id) => {
  if (category === "travel") return `/travel/${id}`;
  if (category === "training") return `/training/request/${id}`;
  return `/claim/${id}`;
};
